import React, { createContext, useContext, useState, useCallback } from 'react';
import { exploreService } from '../components/FrondDetails/Discover/Explore/exploreService';

const ExploreContext = createContext();

export const useExplore = () => {
  const context = useContext(ExploreContext);
  if (!context) {
    throw new Error('useExplore must be used within ExploreProvider');
  }
  return context;
};

export const ExploreProvider = ({ children, token }) => {
  const [categories, setCategories] = useState([]);
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useState({});
  const [userLocation, setUserLocation] = useState(null);

  React.useEffect(() => {
    if (token) {
      exploreService.setToken(token);
    }
  }, [token]);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await exploreService.getExploreCategories();
      setCategories(data);
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const searchPlaces = useCallback(async (params) => {
    setLoading(true);
    setError(null);
    try {
      const data = await exploreService.searchNearbyPlaces(params);
      setPlaces(data.places || []);
      setSearchParams(params);
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const explorePlaces = useCallback(async (params) => {
    setLoading(true);
    setError(null);
    try {
      const data = await exploreService.explorePlaces(params);
      setPlaces(data.places || []);
      setSearchParams(params);
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const searchNearMe = useCallback(async (params) => {
    setLoading(true);
    setError(null);
    try {
      const data = await exploreService.searchNearMe(params);
      setPlaces(data.places || []);
      setUserLocation(data.location);
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const reverseGeocode = useCallback(async (latitude, longitude) => {
    setLoading(true);
    setError(null);
    try {
      const data = await exploreService.reverseGeocode(latitude, longitude);
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearResults = useCallback(() => {
    setPlaces([]);
    setError(null);
    setSearchParams({});
  }, []);

  const searchByCity = useCallback(async (city, country = null) => {
    setLoading(true);
    setError(null);
    try {
      const data = await exploreService.searchByCity(city, country);
      setPlaces(data.places || []);
      setSearchParams({ city, country });
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const geocodeCity = useCallback(async (city, country = null) => {
    setLoading(true);
    setError(null);
    try {
      const data = await exploreService.geocodeCity(city, country);
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const value = {
    categories,
    places,
    loading,
    error,
    searchParams,
    userLocation,
    fetchCategories,
    searchPlaces,
    explorePlaces,
    searchNearMe,
    reverseGeocode,
    clearResults,
    searchByCity,
    geocodeCity,
  };

  return (
    <ExploreContext.Provider value={value}>
      {children}
    </ExploreContext.Provider>
  );
};